import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../api";
import Glyph from "./navGlyphs";
import PhoneNotifications from "../components/PhoneNotifications";

/*
 * SETTINGS — behind the Gabriel block at the foot of the sidebar.
 *
 * Built against Gabriel's mockup (Navigation.dc.html, screen 02): three tabs,
 * each a card of rows with a name, what it is for, its state and an action.
 * Every row comes from GET /settings/overview and is a fact about this
 * deployment (src/settingsOverview.js), so a connection that reads "On" is one
 * whose key is actually set, and Google's row names any permission the grant
 * is missing.
 *
 * WHAT THE OLD PAGE DID IS KEPT: the Google connect / sync / disconnect
 * controls sit on Google's row, and the phone-notification card is the
 * Notifications tab, unchanged.
 *
 * Departures from the mockup, toward what exists: its LinkedIn Lead Gen Forms
 * row, its 09:00 approvals digest and its "empty room" phone alert are not here
 * because none of them exists; Webflow forms, Apollo, Claude, the audit tool,
 * LinkedIn conversions and HubSpot are, because they do. A template fixed in
 * code says so instead of offering an Edit that would go nowhere.
 */

const TABS = [
  { key: "connections", label: "Connections", glyph: "plug" },
  { key: "templates", label: "Templates", glyph: "fileText" },
  { key: "notifications", label: "Notifications", glyph: "bell" },
];

const STATE_LABEL = { on: "Connected", partial: "Partly", error: "Error", off: "Off" };

function dateShort(value) {
  return value ? new Date(value).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : null;
}

function State({ state }) {
  return <span className={`crm-set__state crm-set__state--${state}`}>{STATE_LABEL[state] || state}</span>;
}

/* ---------------------------------------------------------- Google row */

function GoogleRow({ row, onChanged }) {
  const [busy, setBusy] = useState(null);
  const [note, setNote] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const reconnect = async () => {
    setBusy("reconnect");
    try {
      const { url } = await api("/gmail/auth");
      window.location.href = url;
    } catch (err) {
      setNote(err.message);
      setBusy(null);
    }
  };
  const sync = async () => {
    setBusy("sync");
    setNote(null);
    try {
      const r = await api("/gmail/sync", { method: "POST" });
      setNote(`Checked ${r.checked || 0} leads · ${r.updated || 0} updated · ${r.created || 0} new`);
      onChanged();
    } catch (err) {
      setNote(err.message);
    } finally {
      setBusy(null);
    }
  };
  const disconnect = async () => {
    setBusy("disconnect");
    try {
      await api("/gmail/disconnect", { method: "POST" });
      setConfirming(false);
      onChanged();
    } catch (err) {
      setNote(err.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="crm-set__row crm-set__row--wide">
      <div className="crm-set__rowmain">
        <div className="crm-set__text">
          <b>{row.name}</b>
          <span>{row.what}</span>
        </div>
        <State state={row.state} />
        <button type="button" className="crm-set__btn" onClick={reconnect} disabled={!!busy}>
          {row.state === "off" ? "Connect" : "Reconnect"}
        </button>
      </div>
      <p className={`crm-set__detail${row.state === "on" ? "" : " is-warn"}`}>{row.detail}</p>
      {row.state !== "off" && (
        <>
          <div className="crm-set__perms">
            {row.permissions.map((p) => (
              <span key={p.scope} className={p.granted ? "is-on" : "is-off"} title={p.used_for}>
                {p.granted ? "✓" : "×"} {p.label}
              </span>
            ))}
          </div>
          <div className="crm-set__subactions">
            <span className="crm-set__muted">
              {row.last_sync ? `Last sync ${new Date(row.last_sync).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}` : "Not synced yet"}
            </span>
            <button type="button" className="crm-set__link" onClick={sync} disabled={!!busy}>
              {busy === "sync" ? "Syncing…" : "Sync now"}
            </button>
            {confirming ? (
              <>
                <span className="crm-set__muted">Stops all mail and calendar reads.</span>
                <button type="button" className="crm-set__link crm-set__link--danger" onClick={disconnect} disabled={!!busy}>
                  Yes, disconnect
                </button>
                <button type="button" className="crm-set__link" onClick={() => setConfirming(false)}>
                  Cancel
                </button>
              </>
            ) : (
              <button type="button" className="crm-set__link" onClick={() => setConfirming(true)} disabled={!!busy}>
                Disconnect
              </button>
            )}
          </div>
        </>
      )}
      {note && <p className="crm-set__muted">{note}</p>}
    </div>
  );
}

/* ------------------------------------------------------------ the page */

export default function SettingsPage() {
  const [params, setParams] = useSearchParams();
  const tab = TABS.some((t) => t.key === params.get("tab")) ? params.get("tab") : "connections";
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  // Google's consent screen sends him back here with ?gmail=connected or
  // ?gmail=error; the answer is said once, then the marker is dropped.
  const [oauth] = useState(() => params.get("gmail"));

  const load = useCallback(() => {
    api("/settings/overview")
      .then(setData)
      .catch((err) => setError(err.message));
  }, []);

  useEffect(() => {
    load();
    // Back from Google's consent screen: drop the marker the OAuth callback adds.
    if (params.get("gmail")) setParams({ tab: "connections" }, { replace: true });
  }, [load]);

  return (
    <div className="crm-set">
      <header className="crm-set__head">
        <span className="crm-set__muted">Gabriel · Founder</span>
        <h1>Settings</h1>
      </header>

      <div className="crm-set__tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={t.key === tab}
            className={t.key === tab ? "is-on" : ""}
            onClick={() => setParams({ tab: t.key }, { replace: true })}
          >
            <Glyph name={t.glyph} size={15} />
            {t.label}
          </button>
        ))}
      </div>

      {oauth === "connected" && <p className="crm-set__ok">Google is connected.</p>}
      {oauth === "error" && <p className="crm-set__bad">Google didn't connect. Try Reconnect again, and allow every permission it asks for.</p>}
      {error && <p className="crm-set__bad">{error}</p>}
      {!data && !error && <p className="crm-set__muted">Reading settings…</p>}

      {data && tab === "connections" && (
        <section className="crm-set__card">
          {data.connections.map((row) =>
            row.key === "google" ? (
              <GoogleRow key={row.key} row={row} onChanged={load} />
            ) : (
              <div key={row.key} className="crm-set__row crm-set__row--wide">
                <div className="crm-set__rowmain">
                  <div className="crm-set__text">
                    <b>{row.name}</b>
                    <span>{row.what}</span>
                  </div>
                  <State state={row.state} />
                </div>
                <p className={`crm-set__detail${row.state === "on" ? "" : " is-warn"}`}>
                  {row.detail}
                  {row.state !== "on" && row.set_in ? <span className="crm-set__muted"> Set in Railway: {row.set_in}.</span> : null}
                </p>
              </div>
            )
          )}
        </section>
      )}

      {data && tab === "templates" && (
        <section className="crm-set__card">
          {data.templates.map((row) => (
            <div key={row.key} className="crm-set__row">
              <div className="crm-set__rowmain">
                <div className="crm-set__text">
                  <b>{row.name}</b>
                  <span>{row.what}</span>
                </div>
                <span className="crm-set__muted crm-set__when">
                  {row.fixed ? "Fixed in code" : row.edited_at ? `Edited ${dateShort(row.edited_at)}` : ""}
                </span>
                {row.to && (
                  <Link to={row.to} className="crm-set__btn">
                    Edit
                  </Link>
                )}
              </div>
            </div>
          ))}
        </section>
      )}

      {data && tab === "notifications" && (
        <>
          <section className="crm-set__card">
            <div className="crm-set__row">
              <div className="crm-set__rowmain">
                <div className="crm-set__text">
                  <b>Slack new-lead message</b>
                  <span>Every new lead, with its priority band from Inbound ▸ Scoring</span>
                </div>
                <State state={data.notifications.slack.on ? "on" : "off"} />
              </div>
            </div>
          </section>
          <section className="crm-set__card crm-set__card--pad">
            <PhoneNotifications />
          </section>
        </>
      )}
    </div>
  );
}
